import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiTrash2, 
  FiX, 
  FiImage, 
  FiCheckCircle, 
  FiAlertCircle,
  FiLayout
} from 'react-icons/fi';
import Sidebar from '../../components/Admin/Sidebar';
import SafeIcon from '../../common/SafeIcon';
import ncb from '../../db/ncb';

const GalleryManagement = () => {
  const [images, setImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  
  const [formData, setFormData] = useState({
    url: '',
    alt_text: '',
    span_class: '',
    display_order: 0
  });

  const loadImages = () => {
    const data = ncb.gallery.list();
    setImages(data);
    setFormData(prev => ({ ...prev, display_order: data.length + 1 }));
  };

  useEffect(() => {
    loadImages();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      ncb.gallery.add(formData);
      showNotification('Photo added to gallery');
      setIsModalOpen(false);
      setFormData({ url: '', alt_text: '', span_class: '', display_order: images.length + 2 });
      loadImages();
    } catch (err) {
      showNotification('Error adding photo', 'error');
    }
    setLoading(false);
  };

  const deleteImage = (id) => {
    if (window.confirm('Are you sure you want to remove this photo?')) {
      ncb.gallery.delete(id);
      showNotification('Photo removed');
      loadImages();
    }
  };

  const layoutOptions = [
    { label: 'Standard (1x1)', value: '' },
    { label: 'Wide (2x1)', value: 'md:col-span-2' },
    { label: 'Tall (1x2)', value: 'md:row-span-2' },
    { label: 'Large (2x2)', value: 'md:col-span-2 md:row-span-2' },
  ];

  return (
    <div className="flex bg-brand-cream min-h-screen">
      <Sidebar />
      
      <main className="flex-grow p-8">
        <header className="mb-10 flex justify-between items-center">
          <div>
            <h1 className="font-serif text-3xl text-brand-dark font-bold">Gallery Management</h1>
            <p className="text-brand-sage text-sm">Organize your visuals for the public website.</p>
          </div>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-brand-olive text-white px-6 py-3 rounded-sm flex items-center gap-2 uppercase tracking-widest text-xs font-bold hover:bg-brand-dark transition-all shadow-lg active:scale-95"
          >
            <SafeIcon icon={FiPlus} /> Add New Photo
          </button>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images.map((img) => (
            <motion.div 
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              key={img.id}
              className="group relative bg-white aspect-square rounded-sm overflow-hidden border border-brand-light/30 shadow-sm"
            >
              <img src={img.url} alt={img.alt_text} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-brand-dark/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3 p-4 text-center">
                <p className="text-white text-[10px] uppercase tracking-widest font-bold">{img.alt_text}</p>
                <div className="flex items-center gap-2">
                   <span className="bg-brand-olive/80 text-[8px] text-white px-2 py-1 rounded-full uppercase tracking-tighter">
                    {layoutOptions.find(o => o.value === img.span_class)?.label || 'Standard'}
                   </span>
                </div>
                <button 
                  onClick={() => deleteImage(img.id)}
                  className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal Form */}
        <AnimatePresence>
          {isModalOpen && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-brand-dark/60 backdrop-blur-sm"
                onClick={() => setIsModalOpen(false)}
              />
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative bg-white w-full max-w-lg rounded-sm shadow-2xl p-8"
              >
                <div className="flex justify-between items-center mb-8">
                  <h2 className="font-serif text-2xl text-brand-dark">Add Gallery Photo</h2>
                  <button onClick={() => setIsModalOpen(false)} className="text-brand-sage hover:text-brand-dark">
                    <SafeIcon icon={FiX} className="text-2xl" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Image URL</label>
                    <div className="relative">
                      <SafeIcon icon={FiImage} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-light" />
                      <input 
                        type="url" 
                        required 
                        className="w-full pl-10 pr-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm"
                        value={formData.url}
                        onChange={(e) => setFormData({...formData, url: e.target.value})}
                        placeholder="https://images.unsplash.com/..."
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Caption / Alt Text</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm"
                      value={formData.alt_text}
                      onChange={(e) => setFormData({...formData, alt_text: e.target.value})}
                      placeholder="e.g. Living Area View"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Layout Style</label>
                      <div className="relative">
                        <SafeIcon icon={FiLayout} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-light pointer-events-none" />
                        <select 
                          className="w-full pl-10 pr-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none text-sm appearance-none cursor-pointer"
                          value={formData.span_class}
                          onChange={(e) => setFormData({...formData, span_class: e.target.value})}
                        >
                          {layoutOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-brand-sage mb-2 font-bold">Display Order</label>
                      <input 
                        type="number" 
                        className="w-full px-4 py-3 bg-brand-cream/30 border border-brand-light/50 focus:border-brand-olive outline-none transition-colors text-sm"
                        value={formData.display_order}
                        onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full bg-brand-olive text-white py-4 uppercase tracking-[0.2em] font-bold hover:bg-brand-dark transition-all shadow-lg active:scale-[0.98]"
                  >
                    {loading ? 'Adding...' : 'Add to Gallery'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Notifications */}
        <AnimatePresence>
          {notification && (
            <motion.div 
              initial={{ opacity: 0, y: 50, x: '-50%' }}
              animate={{ opacity: 1, y: 0, x: '-50%' }}
              exit={{ opacity: 0, y: 50, x: '-50%' }}
              className={`fixed bottom-8 left-1/2 z-[200] px-6 py-3 rounded-sm shadow-2xl flex items-center gap-3 text-white text-sm font-bold uppercase tracking-widest ${notification.type === 'error' ? 'bg-red-500' : 'bg-brand-olive'}`}
            >
              <SafeIcon icon={notification.type === 'error' ? FiAlertCircle : FiCheckCircle} />
              {notification.message}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default GalleryManagement;